import { z } from 'zod';

import { defineNode, passThrough } from '../define';
import { PLATFORMS } from '../types';

export const filter = defineNode({
  type: 'filter.filter',
  version: 1,
  category: 'filter',
  label: 'Filter',
  description: 'Keep items by platform, format, length, language or date.',
  icon: 'Funnel',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [{ id: 'out', type: 'same' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    platforms: z
      .array(z.enum(PLATFORMS))
      .default([])
      .meta({ title: 'Platforms', widget: 'chips' }),
    minSeconds: z.int().min(0).max(600).optional().meta({ title: 'At least (s)' }),
    maxSeconds: z.int().min(1).max(600).optional().meta({ title: 'At most (s)' }),
    maxAgeDays: z
      .int()
      .min(1)
      .max(365)
      .optional()
      .meta({ title: 'Posted in the last', widget: 'days' }),
  }),
  cardinality: passThrough,
});

export const threshold = defineNode({
  type: 'filter.threshold',
  version: 1,
  category: 'filter',
  label: 'Threshold',
  description: 'Keep items that pass a metric rule — views ≥ 50K, lift ≥ 2×.',
  icon: 'SlidersHorizontal',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['source'] }],
  outputs: [{ id: 'out', type: 'source' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    metric: z.enum(['lift', 'views', 'watchTime', 'saves']).default('lift').meta({
      title: 'Metric',
      widget: 'segmented',
    }),
    min: z.number().min(0).default(2).meta({ title: 'At least' }),
    windowDays: z
      .int()
      .min(1)
      .max(90)
      .default(7)
      .meta({ title: 'Within', widget: 'days' }),
  }),
  cardinality: passThrough,
});

export const cooldown = defineNode({
  type: 'filter.cooldown',
  version: 1,
  category: 'filter',
  label: 'Cooldown',
  description: 'Skip sources already used in the last N days.',
  icon: 'Snowflake',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['source'] }],
  outputs: [{ id: 'out', type: 'source' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    days: z
      .int()
      .min(1)
      .max(365)
      .default(30)
      .meta({ title: 'Used in the last', widget: 'days' }),
  }),
  cardinality: passThrough,
});

export const analyse = defineNode({
  type: 'filter.analyse',
  version: 1,
  category: 'filter',
  label: 'Analyse',
  description: 'Break a reel into hook, scenes, transcript, pacing and on-screen text.',
  icon: 'Scan',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['source'] }],
  outputs: [{ id: 'out', type: 'blueprint' }],
  mode: 'map',
  executor: 'llm',
  config: z.object({}),
  cardinality: passThrough,
});

export const FILTERS = [filter, threshold, cooldown, analyse];
